import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Presentation, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import MeasurementForm from "@/components/admin/estimates/MeasurementForm";
import ProductSelector from "@/components/admin/estimates/ProductSelector";
import LineItemsTable from "@/components/admin/estimates/LineItemsTable";
import EstimatePDF from "@/components/admin/estimates/EstimatePDF";
import { CustomerSelector } from "@/components/admin/estimates/CustomerSelector";
import { MeasurementsSummaryCard } from "@/components/admin/estimates/MeasurementsSummaryCard";
import { useEstimates } from "@/hooks/useEstimates";
import { useEstimateCalculations } from "@/hooks/useEstimateCalculations";
import { useEstimateLineItems } from "@/hooks/useEstimateLineItems";
import { useMeasurements } from "@/hooks/useMeasurements";
import { usePriceBooks } from "@/hooks/usePriceBooks";
import { Customer, useCustomers } from "@/hooks/useCustomers";
import { toast } from "@/hooks/use-toast";

const EstimateBuilder = () => {
  const navigate = useNavigate();
  const { id: estimateId } = useParams();
  const { estimates, createEstimate, updateEstimate } = useEstimates();
  const { measurements } = useMeasurements(estimateId || undefined);
  const { priceBooks } = usePriceBooks();

  const [customerId, setCustomerId] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("Suffolk");
  const [zipCode, setZipCode] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("draft");
  const [taxRate] = useState(8.625);
  const [activeTab, setActiveTab] = useState("customer");
  
  // Track measurement data from selected customer
  const [selectedCustomerMeasurementData, setSelectedCustomerMeasurementData] = useState<any>(null);
  const [selectedCustomerMeasurementSource, setSelectedCustomerMeasurementSource] = useState<string | null>(null);
  const [selectedCustomerMeasurementFileUrl, setSelectedCustomerMeasurementFileUrl] = useState<string | null>(null);
  const [selectedCustomerMeasurementPdfUrl, setSelectedCustomerMeasurementPdfUrl] = useState<string | null>(null);
  const [selectedCustomerProjectPhotos, setSelectedCustomerProjectPhotos] = useState<any[]>([]);

  const { lineItems, addLineItem, addMultipleLineItems, replaceOrAddLineItem } = useEstimateLineItems(estimateId || undefined);
  const { subtotal, taxAmount, total } = useEstimateCalculations(lineItems, taxRate);
  
  const measurement = measurements && measurements.length > 0 ? measurements[0] : null;

  // Load estimate data if editing
  useEffect(() => {
    if (estimateId && estimates) {
      const estimate = estimates.find((e) => e.id === estimateId);
      if (estimate) {
        setCustomerId(estimate.customer_id || "");
        setCustomerName(estimate.customer_name);
        setCustomerEmail(estimate.customer_email || "");
        setCustomerPhone(estimate.customer_phone);
        setCustomerAddress(estimate.customer_address);
        setCity(estimate.city);
        setCounty(estimate.county);
        setZipCode(estimate.zip_code);
        setNotes(estimate.notes || "");
        setStatus(estimate.status || "draft");
        
        // Load measurement data if present
        if (estimate.measurement_file_url) {
      setSelectedCustomerMeasurementData(estimate.measurement_data);
      setSelectedCustomerMeasurementSource(estimate.measurement_source);
      setSelectedCustomerMeasurementFileUrl(estimate.measurement_file_url);
      setSelectedCustomerMeasurementPdfUrl(estimate.measurement_pdf_url);
      setSelectedCustomerProjectPhotos(estimate.project_photos || []);
        }
      }
    }
  }, [estimateId, estimates]);

  // Update estimate totals when line items change
  useEffect(() => {
    if (estimateId && lineItems.length > 0) {
      updateEstimate.mutate({
        id: estimateId,
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
      });
    }
  }, [lineItems, subtotal, taxAmount, total]);

  const handleCustomerSelect = (customer: Customer) => {
    setCustomerId(customer.id);
    setCustomerName(customer.name);
    setCustomerEmail(customer.email || "");
    setCustomerPhone(customer.phone);
    setCustomerAddress(customer.address || "");
    setCity(customer.city || "");
    setCounty(customer.county || "Suffolk");
    setZipCode(customer.zip_code || "");
    
    // Copy measurement data if available
    if (customer.measurement_file_url && customer.measurement_data) {
      setSelectedCustomerMeasurementData(customer.measurement_data);
      setSelectedCustomerMeasurementSource(customer.measurement_source);
      setSelectedCustomerMeasurementFileUrl(customer.measurement_file_url);
      setSelectedCustomerMeasurementPdfUrl(customer.measurement_pdf_url);
      
      toast({
        title: "Measurement data available",
        description: "Customer has measurement file on record",
      });
    } else {
      setSelectedCustomerMeasurementData(null);
      setSelectedCustomerMeasurementSource(null);
      setSelectedCustomerMeasurementFileUrl(null);
      setSelectedCustomerMeasurementPdfUrl(null);
    }

    if (customer.project_photos && customer.project_photos.length > 0) {
      const includedPhotos = customer.project_photos.filter((p: any) => p.include_in_proposal !== false);
      setSelectedCustomerProjectPhotos(includedPhotos);
      
      if (includedPhotos.length > 0) {
        toast({
          title: `${includedPhotos.length} roof photos ready`,
          description: "Will be included in proposal",
        });
      }
    } else {
      setSelectedCustomerProjectPhotos([]);
    }
  };

  const handleSaveDraft = async () => {
    if (!customerName || !customerPhone || !customerAddress || !city || !zipCode) {
      toast({
        title: "Missing required fields",
        description: "Please fill in all required customer information",
        variant: "destructive",
      });
      return;
    }

    // Find the active price book for the selected county
    const activePriceBook = priceBooks?.find(
      pb => pb.county === county && pb.is_active
    );

    if (!activePriceBook) {
      toast({
        title: "No price book found",
        description: `No active price book exists for ${county} county. Please contact support.`,
        variant: "destructive",
      });
      return;
    }

    const estimateData = {
      customer_id: customerId || null,
      estimate_number: `EST-${Date.now()}`,
      customer_name: customerName,
      customer_email: customerEmail || null,
      customer_phone: customerPhone,
      customer_address: customerAddress,
      city,
      county,
      zip_code: zipCode,
      status,
      price_book_id: activePriceBook.id,
      subtotal,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total,
      notes: notes || null,
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      measurement_file_url: selectedCustomerMeasurementFileUrl,
      measurement_source: selectedCustomerMeasurementSource,
      measurement_data: selectedCustomerMeasurementData,
      measurement_pdf_url: selectedCustomerMeasurementPdfUrl,
      project_photos: selectedCustomerProjectPhotos,
    };

    if (estimateId) {
      updateEstimate.mutate({ id: estimateId, ...estimateData });
    } else {
      createEstimate.mutate(estimateData, {
        onSuccess: (data: any) => {
          navigate(`/admin/estimates/builder/${data.id}`, { replace: true });
          toast({
            title: "Draft saved",
            description: "You can now add measurements and products",
          });
          setActiveTab("measurements");
        },
      });
    }
  };

  const handleNextStep = () => {
    if (activeTab === "customer") {
      handleSaveDraft();
    } else if (activeTab === "measurements") {
      setActiveTab("products");
    } else if (activeTab === "products") {
      setActiveTab("review");
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/estimates")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {estimateId ? "Edit Estimate" : "New Estimate"}
            </h1>
            <p className="text-muted-foreground">
              Create detailed estimates with measurements and pricing
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {estimateId && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/estimates/presentation/${estimateId}`)}
              >
                <Presentation className="mr-2 h-4 w-4" />
                Sales Mode
                <Badge variant="secondary" className="ml-2">New</Badge>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/estimates/${estimateId}/proposal`)}
              >
                <FileText className="mr-2 h-4 w-4" />
                Generate Proposal
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handleSaveDraft}>
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="customer">Customer Info</TabsTrigger>
          <TabsTrigger value="measurements" disabled={!estimateId}>Measurements</TabsTrigger>
          <TabsTrigger value="products" disabled={!estimateId}>Products</TabsTrigger>
          <TabsTrigger value="review" disabled={!estimateId}>Review</TabsTrigger>
        </TabsList>

        <TabsContent value="customer" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Existing Customer</Label>
                <CustomerSelector value={customerId} onSelect={handleCustomerSelect} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customerName">Customer Name *</Label>
                  <Input
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customerPhone">Phone *</Label>
                  <Input
                    id="customerPhone"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerEmail">Email</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="john@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerAddress">Address *</Label>
                <Input
                  id="customerAddress"
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="123 Main St"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Smithtown"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="county">County *</Label>
                  <Select value={county} onValueChange={setCounty}>
                    <SelectTrigger id="county">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Nassau">Nassau</SelectItem>
                      <SelectItem value="Suffolk">Suffolk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode">Zip Code *</Label>
                  <Input
                    id="zipCode"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="11787"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleNextStep}>
                  {estimateId ? "Next: Measurements" : "Save & Continue"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="measurements">
          {estimateId ? (
            <>
              <MeasurementForm estimateId={estimateId} />
              <div className="flex justify-end mt-4">
                <Button onClick={handleNextStep}>Next: Products</Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                Please save customer information first
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="products">
          {estimateId ? (
            <div className="space-y-4">
              {measurement && (
                <MeasurementsSummaryCard measurement={measurement} />
              )}
              
              <ProductSelector
                county={county}
                estimateId={estimateId}
                existingLineItemsCount={lineItems.length}
                onAddLineItem={(item) => addLineItem.mutate(item)}
                onAddMultipleLineItems={(items) => addMultipleLineItems.mutate(items)}
                onReplaceOrAddLineItem={(data) => replaceOrAddLineItem.mutate(data)}
              />
              <div className="flex justify-end">
                <Button onClick={handleNextStep}>Next: Review</Button>
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                Please save customer information first
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="review" className="space-y-6">
          {estimateId && <LineItemsTable estimateId={estimateId} />}

          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes or terms..."
                rows={4}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Estimate Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>${subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax ({taxRate}%):</span>
                <span>${taxAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span>${total.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          {estimateId && lineItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Export & Send</CardTitle>
              </CardHeader>
              <CardContent>
                <EstimatePDF estimateId={estimateId} />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EstimateBuilder;
